import MfaCodeField from '@/components/Mfa/MfaCodeField';
import {
  MfaMethod,
  useMfaAuthenticationOptionsMutation,
  useStartMfaStepUpMutation,
  useVerifyMfaStepUpMutation,
} from '@/generated/graphql';
import { graphQLErrorCode } from '@/utils/apollo-errors';
import { isCompleteCode } from '@/utils/mfa';
import { MFA_ERROR_CODES, mfaErrorMessage } from '@/utils/mfa-errors';
import {
  MFA_METHOD_SWITCH_LABELS,
  usableMfaMethods,
} from '@/utils/mfa-methods';
import { passkeyErrorMessage } from '@/utils/passkeys';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import VpnKeyOutlinedIcon from '@material-ui/icons/VpnKeyOutlined';
import { Alert } from '@material-ui/lab';
import { startAuthentication } from '@simplewebauthn/browser';
import React, { useEffect, useRef, useState } from 'react';

const useStyles = makeStyles((theme) => ({
  progress: {
    position: 'absolute',
    width: '100%',
    top: 0,
    left: 0,
  },
  switches: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(-1),
  },
}));

interface StepUpDialogProps {
  open: boolean;
  /** From auth:mfa_step_up_required. Empty: the user has no factor. */
  methods?: MfaMethod[];
  /** The user verified; the request that needed it runs again. */
  onVerified: () => void;
  onCancel: () => void;
}

/**
 * The in-place step-up: a sensitive action needs a second factor from the
 * last 15 minutes. It starts a step-up (startMfaStepUp), asks for a passkey,
 * an authenticator code or a recovery code like the sign-in step, and
 * verifies it (verifyMfaStepUp). The cookie session is then fresh, and the
 * caller retries.
 */
export default function StepUpDialog({
  open,
  methods,
  onVerified,
  onCancel,
}: StepUpDialogProps): JSX.Element {
  const classes = useStyles();
  const [startMfaStepUp] = useStartMfaStepUpMutation();
  const [verifyMfaStepUp] = useVerifyMfaStepUpMutation();
  const [mfaAuthenticationOptions] = useMfaAuthenticationOptionsMutation();

  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [usable, setUsable] = useState<MfaMethod[]>([]);
  const [method, setMethod] = useState<MfaMethod>(MfaMethod.RecoveryCode);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [noFactor, setNoFactor] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const passkey = method === MfaMethod.Webauthn;
  const recovery = method === MfaMethod.RecoveryCode;
  const kind = recovery ? 'recovery' : 'totp';
  const complete = isCompleteCode(kind, code);
  const others = usable.filter((m) => m !== method);

  /** Starts a new step-up; keeps the method unless `reset`. */
  async function start(reset: boolean): Promise<void> {
    setBusy(true);
    setChallengeId(null);

    try {
      const { data } = await startMfaStepUp();
      const started = data?.startMfaStepUp;
      const next = usableMfaMethods(started?.methods);

      setChallengeId(started?.challengeId ?? null);
      setUsable(next);
      if (reset || !next.includes(method)) {
        setMethod(next[0] ?? MfaMethod.RecoveryCode);
      }
    } catch (err) {
      if (graphQLErrorCode(err) === 'mfa:no-factor') {
        setNoFactor(true);
      } else {
        setError(mfaErrorMessage(err));
      }
    }

    setBusy(false);
  }

  useEffect(() => {
    if (!open) {
      return;
    }

    setCode('');
    setError('');
    setNoFactor(methods?.length === 0);
    if (methods?.length !== 0) {
      start(true);
    }
    // Only when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function switchMethod(next: MfaMethod) {
    setMethod(next);
    setCode('');
    setError('');
    inputRef.current?.focus();
  }

  /** Handles a failed verification; the challenge may need a restart. */
  async function fail(err: unknown, message: string) {
    if (graphQLErrorCode(err) === MFA_ERROR_CODES.challengeInvalid) {
      await start(false);
      setError('That check expired or had too many tries. Try again.');
      return;
    }

    setError(message);
  }

  async function verify(input: { code?: string; credential?: unknown }) {
    await verifyMfaStepUp({
      variables: {
        input: { challengeId: challengeId as string, method, ...input },
      },
    });
    onVerified();
  }

  async function submit(value: string) {
    if (busy || !challengeId) {
      return;
    }

    setBusy(true);
    setError('');

    try {
      await verify({ code: value });
      return;
    } catch (err) {
      await fail(err, mfaErrorMessage(err, { recoveryCode: recovery }));
    }

    setCode('');
    setBusy(false);
    inputRef.current?.focus();
  }

  async function verifyWithPasskey() {
    if (busy || !challengeId) {
      return;
    }

    setBusy(true);
    setError('');

    try {
      const { data } = await mfaAuthenticationOptions({
        variables: { challengeId },
      });
      const credential = await startAuthentication({
        optionsJSON: data?.mfaAuthenticationOptions,
      });

      await verify({ credential });
      return;
    } catch (err) {
      await fail(err, passkeyErrorMessage(err));
    }

    setBusy(false);
  }

  function handleChange(value: string) {
    setCode(value);

    // A full TOTP code submits itself, as on the sign-in step.
    if (!recovery && isCompleteCode(kind, value)) {
      submit(value);
    }
  }

  function handleClose() {
    if (!busy || noFactor) {
      onCancel();
    }
  }

  let text =
    'For your security, enter the 6-digit code from your authenticator app to continue.';
  if (passkey) {
    text =
      'For your security, use your passkey to continue. Your device asks for your fingerprint, face, screen lock or security key.';
  } else if (recovery) {
    text =
      'For your security, enter one of your recovery codes to continue.';
  }
  if (noFactor) {
    text =
      'This action needs two-step verification. Set it up on My Profile, then try again.';
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="step-up-dialog-title"
      maxWidth="xs"
      fullWidth
    >
      <div className={classes.progress}>{busy && <LinearProgress />}</div>
      <DialogTitle id="step-up-dialog-title">Confirm it’s you</DialogTitle>

      <DialogContent>
        <DialogContentText>{text}</DialogContentText>

        {Boolean(error) && (
          <Box mb={2}>
            <Alert severity="error" variant="outlined" role="alert">
              {error}
            </Alert>
          </Box>
        )}

        {!noFactor && (
          <>
            {passkey ? (
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<VpnKeyOutlinedIcon />}
                disabled={busy || !challengeId}
                onClick={verifyWithPasskey}
                autoFocus
              >
                Use a passkey
              </Button>
            ) : (
              <form
                id="step-up-code-form"
                noValidate
                onSubmit={(event) => {
                  event.preventDefault();
                  if (complete) {
                    submit(code);
                  }
                }}
              >
                <MfaCodeField
                  key={method}
                  kind={kind}
                  value={code}
                  onChange={handleChange}
                  inputRef={inputRef}
                  error={Boolean(error) && !code}
                  autoFocus
                />
              </form>
            )}

            {others.length > 0 && (
              <div className={classes.switches}>
                {others.map((other) => (
                  <Button
                    key={other}
                    variant="text"
                    onClick={() => switchMethod(other)}
                    disabled={busy}
                  >
                    {MFA_METHOD_SWITCH_LABELS[other]}
                  </Button>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} size="large" disabled={busy && !noFactor}>
          {noFactor ? 'Close' : 'Cancel'}
        </Button>
        {!noFactor && !passkey && (
          <Button
            type="submit"
            form="step-up-code-form"
            color="primary"
            size="large"
            disabled={busy || !complete || !challengeId}
          >
            Verify
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
